import React, { useMemo, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { Typography, Box, Container, Paper, Stack, Button } from '@mui/material';
import { PlayArrow as PlayArrowIcon } from '@mui/icons-material';
import SEO from '../components/common/SEO';
import VideoModal from '../components/common/VideoModal';
import { useSpeaker } from '../hooks/useSpeakers';
import { utils, Speaker as SpeakerType } from '../services/wordpressApi';
import { proxiedMediaUrl } from '../services/mediaProxy';
import DOMPurify from 'dompurify';
import '../styles/Speaker.css';

interface LocationState {
  speaker?: SpeakerType;
}

const Speaker: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const state = (location.state || {}) as LocationState;
  const [videoModalOpen, setVideoModalOpen] = useState(false);

  // Background refresh; do not block initial render if we have state
  const { speaker: fetchedSpeaker, loading, error } = useSpeaker(slug);

  const initialSpeaker = state.speaker;
  const speaker = useMemo(() => fetchedSpeaker || initialSpeaker || null, [fetchedSpeaker, initialSpeaker]);

  const PlaceholderImage = '/speaker.jpg';

  // Immediate render skeleton if nothing at all
  if (!speaker && loading) {
    return (
      <Container maxWidth="lg">
        <Box className="speaker-skeleton-container">
          <Box className="speaker-skeleton-image" />
          <Stack className="speaker-skeleton-content">
            <Box className="speaker-skeleton-title" />
            <Box className="speaker-skeleton-subtitle" />
            <Box className="speaker-skeleton-line-full" />
            <Box className="speaker-skeleton-line-90" />
            <Box className="speaker-skeleton-line-80" />
          </Stack>
        </Box>
      </Container>
    );
  }

  if (error && !speaker) {
    return (
      <Container maxWidth="lg">
        <Box className="speaker-error-container">
          <Typography variant="h5" className="speaker-error-text">
            {error}
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!speaker) {
    return (
      <Container maxWidth="lg">
        <Box className="speaker-error-container">
          <Typography variant="h5" className="speaker-not-found-text">
            Speaker not found.
          </Typography>
        </Box>
      </Container>
    );
  }

  const name = utils.stripHTML(speaker.title?.rendered || '');
  const company = utils.getACFField(speaker, 'company');
  const rawTopic: string | string[] = utils.getACFField(speaker, 'topic') || '';
  const topics: string[] = (() => {
    if (!rawTopic) return [];
    // Handle both array (new multi-select) and string (legacy comma-separated)
    const topicArray = Array.isArray(rawTopic) ? rawTopic : rawTopic.split(',');
    return topicArray
      .map((t) => utils.stripHTML(utils.decodeHTML(String(t))).trim())
      .filter(Boolean);
  })();
  const feeRange: string = utils.getACFField(speaker, 'fee_range') || '';
  const locationValue: string = utils.getACFField(speaker, 'location') || '';
  const videoUrl: string = utils.getACFField(speaker, 'video_url') || '';
  const imageUrl = (() => {
    if (!speaker) return PlaceholderImage;
    const sliderId = typeof (speaker as any).homepage_slider_image_id === 'number' ? (speaker as any).homepage_slider_image_id : 0;
    if (sliderId && sliderId > 0) return proxiedMediaUrl(sliderId, 'large');
    if ((speaker as any).featured_media && (speaker as any).featured_media > 0) return proxiedMediaUrl((speaker as any).featured_media, 'large');
    // Last resort (may expose origin):
    return (speaker as any).featured_image_url || utils.getACFField(speaker as any, 'speaker_image')?.url || PlaceholderImage;
  })();
  // Prepare rich text fields with sanitization and entity decoding
  const rawBio: string = utils.getACFField(speaker, 'biography') || '';
  const rawExcerptHtml: string = speaker.excerpt?.rendered || '';
  const rawContentHtml: string = speaker.content?.rendered || '';
  // Use full content when available; else fall back to excerpt
  const rawOverviewHtml = rawContentHtml || rawExcerptHtml;
  const overviewHtml: string = (() => {
    const decoded = utils.decodeHTML(rawOverviewHtml);
    // Remove trailing bracketed ellipsis tokens like "[…]" or "&hellip;"
    const cleaned = decoded.replace(/\s*\[\s*(?:…|&hellip;)\s*\]\s*$/i, '');
    return DOMPurify.sanitize(utils.cleanSourceDomain(cleaned));
  })();
  const bioHtml: string = (() => {
    const decoded = utils.decodeHTML(rawBio);
    const cleaned = utils.cleanSourceDomain(decoded);
    const hasTags = /<[^>]+>/.test(cleaned);
    if (hasTags) return DOMPurify.sanitize(cleaned);
    // Plain text: preserve line breaks
    return DOMPurify.sanitize(cleaned.replace(/\n/g, '<br/>'));
  })();

  return (
    <>
      <SEO 
        title={`${name} - CEO Keynotes`}
        description={utils.cleanSourceDomain(utils.stripHTML(utils.decodeHTML(rawContentHtml || rawExcerptHtml))) || `Learn more about ${name}`}
        keywords={`${name}, keynote speaker${company ? `, ${company}` : ''}`}
      />

      <Box className="speaker-page">
        {/* Hero: mobile uses full-bleed, desktop centers a masked square image */}
        <Box className="speaker-hero">
          {/* Centered masked image on desktop */}
          <Box
            component="img"
            src={imageUrl}
            alt={name}
            className="speaker-hero-image"
            onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
              const target = e.target as HTMLImageElement;
              const rawUrl = (speaker as any).featured_image_url || utils.getACFField(speaker as any, 'speaker_image')?.url;
              
              // If the image fails to load (e.g. 404 or blocked domain), try raw URL then fallback to placeholder
              if (rawUrl && target.src !== rawUrl && target.src !== PlaceholderImage) {
                target.src = rawUrl;
              } else if (target.src !== PlaceholderImage) {
                target.src = PlaceholderImage;
              }
            }}
          />

          {/* Subtle bottom gradient only on mobile to aid text legibility */}
          <Box className="speaker-hero-gradient" />

          {/* Title overlay */}
          <Container maxWidth="lg" className="speaker-hero-title-container">
            <Box className="speaker-hero-title-content">
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'flex-start',
                width: '100%',
                gap: 2
              }}>
                <Box>
                  <Typography 
                    variant="h2" 
                    className="speaker-hero-name"
                  >
                    {name}
                  </Typography>
                  {company && (
                    <Typography 
                      variant="h6" 
                      className="speaker-hero-company"
                    >
                      {company}
                    </Typography>
                  )}
                </Box>
                
                {/* Play Video Button */}
                {videoUrl && (
                  <Button
                    onClick={() => setVideoModalOpen(true)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: 'white',
                      backgroundColor: 'rgba(43, 43, 43, 0.3)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      borderRadius: 2,
                      px: { xs: 2, sm: 2 },
                      py: { xs: 1, sm: 1 },
                      fontFamily: '"Manrope", sans-serif',
                      fontWeight: 600,
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      textTransform: 'none',
                      alignSelf: 'flex-start',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        border: '1px solid rgba(255, 255, 255, 0.25)',
                      },
                    }}
                  >
                    <PlayArrowIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                    Play Video
                  </Button>
                )}
              </Box>
            </Box>
          </Container>
        </Box>

        {/* Details section */}
        <Container maxWidth="lg" className="speaker-details-container">
          {/* Quick facts row: Fee, Location, Topics - only show if at least one has a value */}
          {((feeRange && feeRange.trim()) || (locationValue && locationValue.trim()) || (topics && topics.length > 0)) && (
            <Paper elevation={0} className="speaker-facts-card" sx={{ borderRadius: '12px' }}>
              <Stack className="speaker-facts-stack">
                {feeRange && feeRange.trim() && (
                  <>
                    <Box>
                      <Typography variant="overline" className="speaker-fact-label">Fee</Typography>
                      <Typography variant="h3" className="speaker-fact-value">{feeRange}</Typography>
                    </Box>
                    {((locationValue && locationValue.trim()) || (topics && topics.length > 0)) && (
                      <div className="speaker-facts-divider" />
                    )}
                  </>
                )}
                {locationValue && locationValue.trim() && (
                  <>
                    <Box>
                      <Typography variant="overline" className="speaker-fact-label">Travels From</Typography>
                      <Typography variant="h3" className="speaker-fact-value">{locationValue}</Typography>
                    </Box>
                    {(topics && topics.length > 0) && (
                      <div className="speaker-facts-divider" />
                    )}
                  </>
                )}
                {topics && topics.length > 0 && (
                  <Box>
                    <Typography variant="overline" className="speaker-fact-label">Topics</Typography>
                    <Typography variant="h3" className="speaker-fact-value">{topics.join(', ')}</Typography>
                  </Box>
                )}
              </Stack>
            </Paper>
          )}

          {/* Summary / Overview */}
          {overviewHtml && (
            <Box className="speaker-section">
              <Typography variant="h5" className="speaker-section-title">
                Overview
              </Typography>
              <Typography variant="body1" className="speaker-section-content" component="div" dangerouslySetInnerHTML={{ __html: overviewHtml }} />
            </Box>
          )}

          {/* Call to Action Section */}
          <Box className="speaker-cta-section">
            <Paper elevation={0} sx={{ 
              background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '12px',
              padding: 4,
              textAlign: 'center'
            }}>
         
              <Typography variant="body1" sx={{ 
                color: 'rgba(255,255,255,0.8)', 
                mb: 3,
                maxWidth: '600px',
                mx: 'auto',
                fontFamily: '"Newsreader", serif'
              }}>
                Reach out for personalized assistance with availability, pricing, and logistics.
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate(`/contact?speaker=${encodeURIComponent(name)}`)}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontFamily: '"Manrope", sans-serif',
                  fontWeight: 600,
                  fontSize: { xs: '0.9rem', sm: '1.0rem', md: '1.1rem' },
                  textTransform: 'none',
                  borderRadius: 2,
                  bgcolor: 'white',
                  color: 'black',
                  '&:hover': {
                    bgcolor: 'rgba(163, 163, 163, 0.9)',
                  },
                }}
              >
                I'm interested in {name}
              </Button>
            </Paper>
          </Box>

          {/* Biography */}
          {bioHtml && (
            <Box className="speaker-bio-section">
              <Typography variant="h5" className="speaker-bio-title">
                Biography
              </Typography>
              <Typography variant="body1" className="speaker-bio-content" component="div" dangerouslySetInnerHTML={{ __html: bioHtml }} />
            </Box>
          )}
        </Container>
      </Box>

      {/* Video Modal */}
      {videoUrl && (
        <VideoModal
          open={videoModalOpen}
          onClose={() => setVideoModalOpen(false)}
          videoUrl={videoUrl}
          title={`${name} - Keynote Speaker Video`}
        />
      )}
    </>
  );
};

export default Speaker;